<script setup lang="ts">
import './css/order.css'
import LoadingSpinner from './components/base/LoadingSpinner.vue'
import NotificationContainer from './components/notification/NotificationContainer.vue'
import OrderProfileLegal from './components/account/order/OrderProfileLegal.vue'
import OrderProfileNatural from './components/account/order/OrderProfileNatural.vue'
import { AccountDataState } from './typings'
import { amountFormatter } from './utilities/helpers'
import { getOrders } from './utilities/api'
import { isLegalProfile } from './utilities/investment-process/useUserProfileType'
import { onMounted, ref } from 'vue'
import { replaceCurrencyWithCurrencySymbol } from './utilities/helpers'
import { useLegalAccountDataState } from './utilities/account/useAccountDataLegal'
import { usePersonalAccountDataState } from './utilities/account/useAccountDataPersonal'
import AccountPanelPersonalPendingInvestmentProcessAlert from './components/account/personal/AccountPanelPersonalPendingInvestmentProcessAlert.vue'
import { isCertificateEnabled } from './utilities/usePlatformSettings'

//@ts-ignore
const accountState: AccountDataState = isLegalProfile() ? useLegalAccountDataState() : usePersonalAccountDataState()

const orders = ref([])
const detailsToggler = ref([])

onMounted(async () => {
    await accountState.fetchAccountData()
    const res = await getOrders(accountState.accountId)
    console.log(res.data)
    orders.value = res.data.orders
})

function condaGetOrderStatus(status: number): string {
    switch (status) {
        case -1:
            return 'order_status_canceled'
        case 0:
            return 'order_status_pending'
        case 1:
            return 'order_status_paid'
        case 2:
            return 'order_status_approved'
        case 3:
            return 'order_status_covered'
        default:
            return 'unknown'
    }
}

function condaGetColorForOrderStatus(status: number): string {
    switch (status) {
        case -1:
            return 'text-red'

        case 0:
            return 'text-orange'

        case 1:
            return 'text-secondary'

        case 2:
            return 'text-secondary-light'

        case 3:
        default:
            return 'text-gray'
    }
}

function investifyIsIcoSystem(system) {
    if (system) {
        if (system.name) {
            if (system.name === 'ICO') {
                return true
            }
        }
        if (system === 'ICO') {
            return true
        }
    }
    return false
}

function investifyIsCfSystem(system) {
    if (system) {
        if (system.name) {
            if (system.name === 'CF') {
                return true
            }
        }
        if (system === 'CF') {
            return true
        }
    }
    return false
}

function condaGetIconForOrderStatus(status: number): string {
    switch (status) {
        case -1:
            return 'alert'

        case 0:
            return 'clock'

        case 1:
            return 'cash'

        case 2:
            return 'checkmark'

        case 3:
        default:
            return 'help'
    }
}

function investifyAgioDisagioTitle(
    agio_disagio_type: number,
    agio_disagio_rate: string,
    agio_disagio_title: string
): string {
    const agio_disagio_amount =
        agio_disagio_type === 1 ? `${parseFloat(agio_disagio_rate).toFixed(2)} €` : `${agio_disagio_rate}%`
    return agio_disagio_title.replace('%s', agio_disagio_amount)
}

function investifyAgioDisagioAmount(
    agio_disagio: number,
    agio_disagio_type: number,
    agio_disagio_rate: number,
    invested_amount: number,
    total_amount
): number {
    let agio_disagio_amount
    if (agio_disagio_type === 1) {
        agio_disagio_amount = agio_disagio_rate
    } else {
        agio_disagio_amount =
            agio_disagio === 1 ? (agio_disagio_rate / 100) * invested_amount : (agio_disagio_rate / 100) * total_amount
    }

    return agio_disagio_amount
}

function toggleDetails(index): void {
    detailsToggler.value[index] = !detailsToggler.value[index]
}

function getHeight(index) {
    return document.querySelector(`#conda_collapse_${index}`).scrollHeight + 'px'
}
</script>

<template>
    <NotificationContainer />
    <main v-if="accountState.loaded" class="orders">
        <!-- Page header -->
        <section class="sticky-anchor sticky-tabs w100 bg-white">
            <!-- Tabs Orders/Assets/Wallets -->

            <div class="alert grid w-full grid-cols-1 space-y-3 pb-4">
                <AccountPanelPersonalPendingInvestmentProcessAlert></AccountPanelPersonalPendingInvestmentProcessAlert>
            </div>

            <div class="orders-sticky-div tabs-menu sticky bg-white">
                <div class="wrapper">
                    <div class="tabs-wrapp">
                        <div class="investify-mobile-tabs">
                            <ul class="orders-page tabs d-flex ml-0 list-none flex-wrap">
                                <li
                                    class="tabs-item active-text-gray-blue active-border-secondary border-hover-secondary new-tablinks tablinks text-gray active bg-white text-center"
                                    data-target-tab="myOrders"
                                    data-target-tab-name="My Orders">
                                    <h2 class="mb-0 py-3">
                                        <span class="float-left"></span>{{ $t('my_orders_side') }}
                                    </h2>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
        </section>
        <!-- End Page header -->
        <section class="account-dashboard bg-gray-lightest py-5">
            <div class="tabscontent-wrapp wrapper">
                <div class="row">
                    <section class="c12 c8-lg orders-content">
                        <div v-for="(order, index) in orders" :key="index">
                            <div class="box-shadow clearfix mb-5 bg-white p-3">
                                <!-- Single Order -->
                                <div class="row d-flex align-items-center flex-wrap justify-between">
                                    <div class="c12 c7-md c7-lg">
                                        <!-- Company Info -->
                                        <div class="d-flex align-items-center">
                                            <div class="spot-small mr-3">
                                                <img
                                                    :src="order.company_logo"
                                                    alt="Company logo"
                                                    class="border-box responsive rounded" />
                                            </div>
                                            <div class="flex-1">
                                                <h5 class="text-gray-blue mb-0">{{ order.asset_data.company_name }}</h5>
                                                <h6 class="text-gray mb-0">{{ order.asset_data.asset_title }}</h6>
                                            </div>
                                        </div>
                                    </div>
                                    <!-- End Company Info -->
                                    <div class="c12 c5-md c5-lg d-flex align-items-center my-2 justify-between">
                                        <p :class="`mb-0 ${condaGetColorForOrderStatus(order.status)} mr-2`">
                                            <span
                                                :class="`ion-${condaGetIconForOrderStatus(order.status)} mr-2`"></span>
                                            {{ $t(condaGetOrderStatus(order.status)) }}
                                        </p>
                                        <p
                                            :class="`conda_order_collapse accordion-title text-gray-dark bold small mb-0 text-right ${
                                                detailsToggler[index] === true ? 'active' : ''
                                            }`"
                                            :data-open-collapse="`conda_collapse_${index}`"
                                            aria-expanded="false"
                                            @click="toggleDetails(index)">
                                            {{ $t('view_details') }}
                                        </p>
                                    </div>
                                </div>
                                <hr />
                                <div class="row d-flex align-items-center flex-wrap justify-between">
                                    <div class="c12 c4-md d-flex align-items-center my-2">
                                        <p class="mb-0 mr-3">
                                            <span class="bold text-gray-blue">{{ $t('date') }}: </span>
                                            <span class="text-gray">{{
                                                new Date(order.created_on).toLocaleDateString('at', {
                                                    day: '2-digit',
                                                    month: '2-digit',
                                                    year: 'numeric',
                                                    timeZone: 'Europe/Vienna',
                                                })
                                            }}</span>
                                        </p>
                                    </div>
                                    <div class="c12 c3-md d-flex align-items-center my-2">
                                        <p class="mb-0">
                                            <span class="bold text-gray-blue">{{ $t('time') }}: </span>
                                            <span class="text-gray">{{
                                                new Date(order.created_on).toLocaleTimeString('at', {
                                                    timeZone: 'Europe/Vienna',
                                                })
                                            }}</span>
                                        </p>
                                    </div>
                                    <div class="c12 c5-md my-2 text-right">
                                        <p class="mb-0">
                                            <span class="bold large text-gray-blue">{{ $t('total') }}: </span>
                                            <span class="bold large text-secondary">
                                                {{ amountFormatter(order.amount) }}
                                                {{ replaceCurrencyWithCurrencySymbol(order.asset_data.currency) }}
                                            </span>
                                        </p>
                                        <p v-if="order.total_amount_to_pay !== order.total_amount" class="mb-0">
                                            <span class="bold large text-gray-blue"
                                                >{{ $t('investment_process.amount_to_pay') }}:
                                            </span>
                                            <span class="bold medium text-secondary" style="white-space: nowrap">
                                                {{ amountFormatter(order.total_amount_to_pay) }}
                                                {{ replaceCurrencyWithCurrencySymbol(order.asset_data.currency) }}
                                            </span>
                                        </p>
                                    </div>
                                </div>
                                <hr />
                                <div
                                    :id="`conda_collapse_${index}`"
                                    class="accordion-content slideable"
                                    :style="`
                                    overflow: hidden;
                                    height: ${detailsToggler[index] ? getHeight(index) : '0px'};
                                    transition-property: height;
                                    transition-duration: 0.5s;
                                    transition-timing-function: ease-in-out;
                                `">
                                    <!-- Hidden content -->
                                    <div class="collapse-measure">
                                        <div class="investment-section">
                                            <!-- Investment details section -->
                                            <div class="wraper-fluid">
                                                <div class="row">
                                                    <div class="c12 my-3">
                                                        <h5 class="large text-gray-blue">{{ $t('investment') }}</h5>
                                                        <hr />
                                                        <ul class="ml-0 list-none p-0">
                                                            <li class="d-flex mb-0 justify-between">
                                                                <p class="large bold mb-0 text-secondary">
                                                                    {{ $t('total') }}:
                                                                </p>
                                                                <p class="large bold mb-0 text-secondary">
                                                                    {{ amountFormatter(order.total_amount) }}
                                                                    {{
                                                                        replaceCurrencyWithCurrencySymbol(
                                                                            order.asset_data.currency
                                                                        )
                                                                    }}
                                                                </p>
                                                            </li>
                                                            <hr />
                                                            <li
                                                                v-if="!order.installments && order.agio_disagio !== 0"
                                                                class="d-flex mb-0 justify-between">
                                                                <p class="text-gray-blue bold mb-0">
                                                                    {{
                                                                        investifyIsCfSystem(
                                                                            order.asset_data.clearing_system
                                                                        )
                                                                            ? $t('investment_amount')
                                                                            : $t('support_amount')
                                                                    }}
                                                                </p>
                                                                <p class="bold mb-0 text-secondary">
                                                                    {{ amountFormatter(order.amount) }}
                                                                    {{
                                                                        replaceCurrencyWithCurrencySymbol(
                                                                            order.asset_data.currency
                                                                        )
                                                                    }}
                                                                </p>
                                                            </li>
                                                            <hr
                                                                v-if="
                                                                    !order.installments && order.agio_disagio !== 0
                                                                " />
                                                            <li
                                                                v-if="order.installments"
                                                                class="d-flex mb-0 justify-between">
                                                                <p class="text-gray-blue bold mb-0">
                                                                    {{ $t('investment_process.initial_payment') }}
                                                                </p>
                                                                <p class="bold mb-0 text-secondary">
                                                                    {{
                                                                        amountFormatter(
                                                                            order.installments.initial_payment
                                                                        )
                                                                    }}
                                                                    {{
                                                                        replaceCurrencyWithCurrencySymbol(
                                                                            order.asset_data.currency
                                                                        )
                                                                    }}
                                                                </p>
                                                            </li>
                                                            <hr v-if="order.installments" />
                                                            <li
                                                                v-if="order.installments"
                                                                class="d-flex mb-0 justify-between">
                                                                <p class="text-gray-blue bold mb-0">
                                                                    {{ $t('investment_process.instalments_period') }}
                                                                </p>

                                                                <p
                                                                    v-if="
                                                                        order.installments.installment_type === 'yearly'
                                                                    "
                                                                    class="text-gray-blue text-lowercase mb-0">
                                                                    {{ order.installments.installment_rates }}
                                                                    {{
                                                                        order.installments.installment_rates === 1
                                                                            ? $t('investment_process.year')
                                                                            : $t('investment_process.years')
                                                                    }}
                                                                </p>

                                                                <p
                                                                    v-if="
                                                                        order.installments.installment_type ===
                                                                        'monthly'
                                                                    "
                                                                    class="text-gray-blue text-lowercase mb-0">
                                                                    {{ order.installments.installment_rates }}
                                                                    {{
                                                                        order.installments.installment_rates === 1
                                                                            ? $t('investment_process.month')
                                                                            : $t('investment_process.months')
                                                                    }}
                                                                </p>
                                                            </li>
                                                            <hr v-if="order.installments" />
                                                            <li
                                                                v-if="order.installments"
                                                                class="d-flex mb-0 justify-between">
                                                                <p class="text-gray-blue bold mb-0">
                                                                    {{ $t('investment_process.instalments_value') }}
                                                                </p>
                                                                <p class="text-gray-blue mb-0">
                                                                    {{
                                                                        amountFormatter(
                                                                            order.installments.installment_value
                                                                        )
                                                                    }}
                                                                    {{
                                                                        replaceCurrencyWithCurrencySymbol(
                                                                            order.asset_data.currency
                                                                        )
                                                                    }}
                                                                </p>
                                                            </li>
                                                            <hr v-if="order.installments" />
                                                            <li
                                                                v-if="order.incentives_title"
                                                                class="d-flex mb-0 justify-between">
                                                                <!-- TODO: Add incentives on the API to the order. -->
                                                                <p class="text-gray-blue bold mb-0">
                                                                    {{ $t('investment_process.instalment_value') }}
                                                                </p>
                                                                <p class="text-gray-blue mb-0">
                                                                    {{ $t('investment_process.silver_package') }}
                                                                </p>
                                                            </li>
                                                            <hr v-if="order.incentives_title" />
                                                            <li
                                                                v-if="
                                                                    order.agio_disagio === 1 || order.agio_disagio === 2
                                                                "
                                                                class="d-flex mb-0 flex-wrap justify-between">
                                                                <p
                                                                    class="investify_agio_disagio_title text-gray-blue bold mb-0">
                                                                    {{
                                                                        investifyAgioDisagioTitle(
                                                                            order.agio_disagio_type,
                                                                            order.agio_disagio_rate,
                                                                            order.agio_disagio_title
                                                                        )
                                                                    }}
                                                                </p>
                                                                <p class="text-gray-blue mb-0">
                                                                    {{
                                                                        amountFormatter(
                                                                            Math.abs(
                                                                                investifyAgioDisagioAmount(
                                                                                    order.agio_disagio,
                                                                                    order.agio_disagio_type,
                                                                                    order.agio_disagio_rate,
                                                                                    order.amount,
                                                                                    order.total_amount
                                                                                )
                                                                            )
                                                                        )
                                                                    }}
                                                                    {{
                                                                        replaceCurrencyWithCurrencySymbol(
                                                                            order.asset_data.currency
                                                                        )
                                                                    }}
                                                                </p>

                                                                <div
                                                                    v-if="order.voucher_code && order.voucher_rate"
                                                                    class="w100 d-flex align-items-center mb-0 justify-between">
                                                                    <p>{{ $t('investment_process.discount') }}</p>
                                                                    <p id="voucher_discount_show">
                                                                        -
                                                                        {{
                                                                            amountFormatter(
                                                                                Math.abs(
                                                                                    (investifyAgioDisagioAmount(
                                                                                        order.agio_disagio,
                                                                                        order.agio_disagio_type,
                                                                                        order.agio_disagio_rate,
                                                                                        order.amount,
                                                                                        order.total_amount
                                                                                    ) /
                                                                                        100) *
                                                                                        order.voucher_rate
                                                                                )
                                                                            )
                                                                        }}
                                                                        {{
                                                                            replaceCurrencyWithCurrencySymbol(
                                                                                order.asset_data.currency
                                                                            )
                                                                        }}
                                                                    </p>
                                                                </div>

                                                                <p class="small text-gray-blue w100 mb-0">
                                                                    {{ order.agio_disagio_disclaimer }}
                                                                </p>
                                                            </li>
                                                            <hr
                                                                v-if="
                                                                    order.agio_disagio === 1 || order.agio_disagio === 2
                                                                " />
                                                            <li
                                                                v-if="order.voucher_amount !== null"
                                                                class="d-flex mb-0 justify-between">
                                                                <p class="medum bold mb-0 text-primary">
                                                                    {{ $t('order_voucher_amount') }}:
                                                                </p>
                                                                <p class="medium bold mb-0 text-primary">
                                                                    -
                                                                    {{ amountFormatter(order.voucher_amount) }}
                                                                    {{
                                                                        replaceCurrencyWithCurrencySymbol(
                                                                            order.asset_data.currency
                                                                        )
                                                                    }}
                                                                </p>
                                                            </li>
                                                            <hr v-if="order.voucher_amount !== null" />
                                                            <li
                                                                v-if="order.discount_voucher_amount !== null"
                                                                class="d-flex mb-0 justify-between">
                                                                <p class="medum bold mb-0 text-primary">
                                                                    {{ $t('order_discount_voucher_amount') }}:
                                                                </p>
                                                                <p class="medium bold mb-0 text-primary">
                                                                    -
                                                                    {{ amountFormatter(order.discount_voucher_amount) }}
                                                                    {{
                                                                        replaceCurrencyWithCurrencySymbol(
                                                                            order.asset_data.currency
                                                                        )
                                                                    }}
                                                                </p>
                                                            </li>
                                                            <hr v-if="order.discount_voucher_amount !== null" />
                                                            <li
                                                                v-if="order.wallet_amount !== null"
                                                                class="d-flex mb-0 justify-between">
                                                                <p class="medum bold mb-0 text-primary">
                                                                    {{ $t('order_wallet_amount') }}:
                                                                </p>
                                                                <p class="medum bold mb-0 text-primary">
                                                                    -
                                                                    {{ amountFormatter(order.wallet_amount) }}
                                                                    {{
                                                                        replaceCurrencyWithCurrencySymbol(
                                                                            order.asset_data.currency
                                                                        )
                                                                    }}
                                                                </p>
                                                            </li>
                                                            <hr v-if="order.wallet_amount !== null" />
                                                            <li
                                                                v-if="order.total_amount_to_pay !== order.total_amount"
                                                                class="d-flex mb-0 justify-between">
                                                                <p class="large bold mb-0 text-secondary">
                                                                    {{ $t('investment_process.amount_to_pay') }}:
                                                                </p>
                                                                <p class="large bold mb-0 text-secondary">
                                                                    {{ amountFormatter(order.total_amount_to_pay) }}
                                                                    {{
                                                                        replaceCurrencyWithCurrencySymbol(
                                                                            order.asset_data.currency
                                                                        )
                                                                    }}
                                                                </p>
                                                            </li>
                                                            <hr
                                                                v-if="
                                                                    order.total_amount_to_pay !== order.total_amount
                                                                " />
                                                        </ul>
                                                    </div>
                                                    <div v-if="order.contract_data_documents.length" class="c12">
                                                        <h5 class="large text-gray-blue">
                                                            Vertragsunterlagen downloaden :
                                                        </h5>
                                                        <hr />
                                                        <ul class="download-contracts-section ml-0 list-none p-0">
                                                            <li
                                                                v-for="(doc, idx) in order.contract_data_documents"
                                                                :key="idx"
                                                                class="my-4">
                                                                <i
                                                                    class="fa fa-file-text-o fa-lg mr-3 text-secondary" />
                                                                <a
                                                                    class="text-gray-blue"
                                                                    :href="doc.url"
                                                                    target="_blank">
                                                                    {{ doc.name }}
                                                                </a>
                                                            </li>
                                                        </ul>
                                                    </div>
                                                    <div v-if="order.reporting_documents.length" class="c12">
                                                        <h5 class="large text-gray-blue">Reporting documents :</h5>
                                                        <hr />
                                                        <ul class="download-contracts-section ml-0 list-none p-0">
                                                            <li
                                                                v-for="(doc, idx) in order.reporting_documents"
                                                                :key="idx"
                                                                class="my-4">
                                                                <i
                                                                    class="fa fa-file-text-o fa-lg mr-3 text-secondary"
                                                                    aria-hidden="true" />
                                                                <a
                                                                    class="text-gray-blue"
                                                                    :href="doc.url"
                                                                    target="_blank">
                                                                    {{ doc.name }}
                                                                </a>
                                                            </li>
                                                        </ul>
                                                    </div>
                                                    <div
                                                        v-if="order.certificate.length && isCertificateEnabled()"
                                                        class="c12">
                                                        <h5 class="large text-gray-blue">
                                                            {{ $t('portfolio.orders.order_certificate') }}
                                                        </h5>
                                                        <hr />
                                                        <ul class="download-contracts-section ml-0 list-none p-0">
                                                            <li
                                                                v-for="(doc, idx) in order.certificate"
                                                                :key="idx"
                                                                class="my-4">
                                                                <i
                                                                    class="fa fa-file-text-o fa-lg mr-3 text-secondary"
                                                                    aria-hidden="true" />
                                                                <a
                                                                    class="text-gray-blue"
                                                                    :href="doc.url"
                                                                    target="_blank">
                                                                    {{ doc.name }}
                                                                </a>
                                                            </li>
                                                        </ul>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <!-- End Investment details section -->
                                    </div>
                                </div>
                                <!-- End Hidden content -->
                                <div v-if="!investifyIsIcoSystem(order.asset_data.clearing_system)">
                                    <div
                                        v-if="
                                            order.payment_methods !== null &&
                                            (order.payment_methods.credit_card == true ||
                                                order.payment_methods.wire_transfer == true)
                                        "
                                        class="row order-payment-methods d-flex my-3 flex-wrap">
                                        <!-- Where order status is 0 -->
                                        <div
                                            v-if="order.payment_methods.wire_transfer === true"
                                            :class="`c12 ${
                                                order.installments && $order.payment_methods.credit_card == true
                                                    ? 'c6-md'
                                                    : ''
                                            } pay-via`">
                                            <!-- c6-md -->
                                            <h4 class="text-gray-blue bold mb-3">
                                                <span class="ion-arrow-swap text-gray mr-2"></span
                                                >{{ $t('investment_process.pay_via_bank_transfer_header') }}
                                            </h4>
                                            <p class="text-gray-blue small">
                                                {{ $t('investment_process.pay_via_bank_transfer_description') }}
                                            </p>
                                            <div class="payment-details my-4">
                                                <p class="mb-2">
                                                    <span class="text-uppercase bold text-secondary"
                                                        >{{ $t('wallet.account_holder') }}:
                                                    </span>
                                                    <span class="text-gray-blue">{{
                                                        order.payment_account_holder ?? 'Lemonway SAS'
                                                    }}</span>
                                                </p>
                                                <p class="mb-2">
                                                    <span class="text-uppercase bold text-secondary">
                                                        {{ $t('wallet.payment_reference') }}:
                                                    </span>
                                                    <span class="text-gray-blue">{{ order.reference_number }}</span>
                                                </p>
                                                <p class="mb-2">
                                                    <span class="text-uppercase bold text-secondary">
                                                        {{ $t('account.iban') }}:
                                                    </span>
                                                    <span class="text-gray-blue">
                                                        {{ order.asset_data.iban }}
                                                    </span>
                                                </p>
                                                <p class="mb-2">
                                                    <span class="text-uppercase bold text-secondary">
                                                        {{ $t('account.bic') }}:
                                                    </span>
                                                    <span class="text-gray-blue">
                                                        {{ order.asset_data.bic }}
                                                    </span>
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div v-else>
                                    <!--  Pay wia ETH and buy ETH -->
                                    <div class="orders row d-flex my-3 flex-wrap justify-center">
                                        <div class="c12 pay-via mb-3 px-4">
                                            <!-- c6-md c6-xl -->
                                            <h4 class="text-gray-blue bold mb-3">
                                                <img src="../public/images/eth3.svg" alt="eth" class="mb-1 mr-2" />{{
                                                    $t('pay_via_eth')
                                                }}
                                            </h4>
                                            <p class="text-gray-blue small">
                                                {{ $t('pay_via_eth_desc') }}
                                            </p>
                                            <div class="payment-details my-4">
                                                <p class="mb-1">
                                                    <span class="text-uppercase bold text-secondary">
                                                        {{ $t('address_to_send_eth') }}:
                                                    </span>
                                                    <span v-if="order.wallet_address" class="text-gray-blue small">{{
                                                        order.wallet_address
                                                    }}</span>
                                                </p>
                                                <p class="mb-1">
                                                    <span class="text-uppercase bold text-secondary"
                                                        >{{ $t('data') }}:</span
                                                    >
                                                    <span class="text-gray-blue"></span>
                                                </p>
                                                <p class="mb-1">
                                                    <span class="text-uppercase bold text-secondary">
                                                        {{ $t('gas_limit') }}:
                                                    </span>
                                                    <span class="text-gray-blue"></span>
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                    <!--  End Pay wia ETH and buy ETH -->
                                </div>
                                <!-- Signature Area for Emission -->
                                <div v-if="order.emission_signatures.length && order.status !== -1">
                                    <h4 class="text-gray-blue bold mb-3">
                                        <span class="ion-edit text-gray mr-2"></span>
                                        {{ $t('signatures.sign_documents') }}
                                    </h4>

                                    <a
                                        v-for="(doc, inner_index) in order.emission_signatures"
                                        :key="inner_index"
                                        :href="doc.link"
                                        target="_blank"
                                        class="i-btn i-btn-secondary i-btn-lg w100-sm my-4 block w-96">
                                        {{ $t('investment_process.download_signature_document') }}
                                    </a>
                                </div>
                                <!-- End Single Order -->
                            </div>
                        </div>
                        <div v-if="!orders.length" class="box-shadow mb-5 bg-white p-3 text-center">
                            <h5 class="text-gray mb-0">{{ $t('no_order') }}</h5>
                        </div>
                    </section>
                    <section class="c12 c4-lg orders-content">
                        <OrderProfileNatural v-if="!isLegalProfile()" />
                        <OrderProfileLegal v-else />
                    </section>
                </div>
            </div>
        </section>
    </main>
    <div v-else class="flex h-[65vh] items-center justify-center">
        <LoadingSpinner class="h-10 w-10 fill-primary" />
    </div>
</template>
